<template>
    <section>
        <FormSection
            :full-height="false"
            title="Employee Scheduler"
            :application-id="visitDetails.Application"
            :show-product-pill="true"
            :show-background="false"
        >
            <template #content>
                <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                    <i class="fa fa-spinner fa-spin font-size-5xl me-2 animated slow flash infinite"></i>
                    <span class="mt-1">
                        <b>Loading...</b>
                    </span>
                </section>

                <section class="task-details" v-else>
                    <div class="container row m-0 p-lg-1 ps-md-2">
                        <div class="form-group col-12 mt-2">
                            <strong for="task-name" class="me-2">Client:</strong>
                            <span class="bold"> {{ visitDetails.FirstName }} {{ visitDetails.LastName }}</span>
                            <p class="mt-2 mb-1">
                                <strong class="me-2">Current/Last Episode Period:</strong>
                                <span>{{ carePeriod }}</span>
                            </p>
                            <p class="mt-2">
                                <strong class="me-2">Agency:</strong>
                                <span>{{ visitDetails.AgencyName }}</span>
                            </p>
                        </div>
                        <div class="form-group col-12"></div>
                        <div class="form-group col-12 col-md-8 col-lg-3 mt-lg-2">
                            <label for="employee"> Visit Type: </label><span class="text-danger">*</span>
                            <TypeaheadInput
                                :getData="availableTasksFilter"
                                :value="selectedTask"
                                v-on:valueChanged="changeTaskList"
                                placeholder="Type to Select Visit Type"
                                :show-all-by-default="true"
                                :open-on-focus="true"
                                :must-select-value="true"
                                v-on:blurredInput="v$.selectedTask?.$touch"
                                :class="`${!v$.selectedTask?.$error ? '' : 'field-invalid'}`"
                            />
                            <span v-if="v$.selectedTask?.$error" class="field-invalid">Visit Type is required.</span>
                        </div>
                        <div class="form-group col-12 col-md-8 col-lg-3 mt-2">
                            <label for="employee"> User/Employee: </label><span class="text-danger">*</span>
                            <TypeaheadInput
                                :getData="employeeListFilter"
                                :value="scheduleEmployee"
                                v-on:valueChanged="changescheduleEmployee"
                                placeholder="Type to Select User"
                                :show-all-by-default="true"
                                :open-on-focus="true"
                                v-on:blurredInput="v$.newVisitDetails?.userId?.$touch"
                                :class="`${!v$.newVisitDetails?.userId?.$error ? '' : 'field-invalid'}`"
                                :disabled="employeeIsPreSelected"
                            />
                            <span v-if="v$.newVisitDetails?.userId?.$error" class="field-invalid"
                                >User/Employee is required.</span
                            >
                        </div>
                        <div class="form-group col-6 col-md-3" v-if="!employeeIsPreSelected">
                            <label> </label>
                            <div class="mt-3">
                                <a href="javascript:void(0);" @click="assignToMe()" class="text-decoration-none"
                                    >Assign to me</a
                                >
                            </div>
                        </div>
                        <div class="w-100"></div>
                        <div class="form-group form-check custom-checkbox col-10 mt-2 ms-3">
                            <div>
                                <input
                                    type="checkbox"
                                    id="billable"
                                    class="form-check-input"
                                    :checked="newVisitDetails.postToAxxessCare"
                                    v-model="newVisitDetails.postToAxxessCare"
                                />
                                <label class="form-check-label" for="billable"> Post to AxxessCARE </label>
                            </div>
                        </div>
                        <div v-if="newVisitDetails.postToAxxessCare" class="form-group col-12 col-md-3 mt-2">
                            <label for="task-name"> Task Type: </label>
                            <select
                                id="task-name"
                                :class="`form-select ${
                                    !v$.newVisitDetails.discipline.$error && newVisitDetails.postToAxxessCare
                                        ? ''
                                        : 'field-invalid mt-0'
                                }`"
                                @change="onTaskTypeChange(newVisitDetails)"
                                v-model="newVisitDetails.discipline"
                            >
                                <option :value="null">Select Task Type</option>
                                <option v-for="(task, index) of specialties" :value="task.id" :key="index">
                                    {{ task.name }}
                                </option>
                            </select>
                            <span
                                v-if="v$.newVisitDetails.discipline?.$error && newVisitDetails.postToAxxessCare"
                                class="field-invalid"
                                >Task Type is required.</span
                            >
                        </div>
                        <div class="form-group col-12">
                            <hr class="ms-0" />
                            <label>Visit Orders</label>
                            <div class="w-50" v-if="visitOrders.length > 0">
                                <table class="table table-responsive-md border border-gray">
                                    <tbody>
                                        <tr class="bg-table-header">
                                            <th>Discipline</th>
                                            <th>Frequency</th>
                                            <th>Effective Date</th>
                                        </tr>
                                        <tr v-for="(order, index) of visitOrders" :key="index">
                                            <td>{{ order.discipline }}</td>
                                            <td class="white-space-pre">{{ order.frequency }}</td>
                                            <td class="white-space-pre">{{ order.effectiveDate }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p v-else>There are no current orders for visits.</p>
                        </div>
                        <div class="form-group col-12">
                            <hr class="ms-0" />
                            <p>
                                To schedule visits for the selected user, click on the desired dates in the calendar
                                below:
                            </p>
                            <div class="form-group col-12 ps-md-2">
                                <MultiDatePickerHH
                                    v-if="currentEpisodeDetails"
                                    :result="visitDates"
                                    :current-episode-details="currentEpisodeDetails"
                                    :visitDetails="visitDetails"
                                    v-on:updateResult="updateResult"
                                    :visitDates="visitDatesList"
                                >
                                    <input class="form-control" />
                                </MultiDatePickerHH>
                                <div v-if="v$.visitDates?.$error" class="invalid-feedback d-block">
                                    Visit Dates is Required.
                                </div>
                            </div>
                            <div class="d-flex col-12 justify-content-center">
                                <div class="form-group col-md-6">
                                    <div class="d-flex flex-wrap flex-md-nowrap">
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend scheduled"></div>
                                            Scheduled
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend completed"></div>
                                            Completed
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend missed"></div>
                                            Missed
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend multiple"></div>
                                            Multiple
                                        </div>
                                        <div class="me-2 me-md-3">
                                            <div class="calendar-legend disabled"></div>
                                            Disabled
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div class="d-flex flex-wrap-reverse flex-md-nowrap justify-content-center my-2" v-if="!isLoading">
                    <span v-if="isSaving">
                        <button class="btn btn-primary">
                            <i class="fa fa-spinner fa-spin animated slow flash infinite"></i>
                        </button>
                    </span>
                    <span v-else>
                        <button class="btn btn-primary" @click="scheduleVisit(SaveEnum.SaveAndExit)">
                            Save and Exit
                        </button>
                        <SaveNotifyUser
                            :agency-id="visitDetails.AgencyId"
                            button-name="Save and Notify Employee"
                            v-on:handleSaveVisit="scheduleVisit(SaveEnum.SaveAndNotifyEmployee)"
                        />
                        <button class="ms-2 btn btn-primary" @click="scheduleVisit(SaveEnum.SaveAndAddAnother)">
                            Save and Add Another
                        </button>
                        <button type="button" class="ms-2 btn btn-md btn-outline-secondary" @click="cancel()">
                            Cancel
                        </button>
                    </span>
                </div>
            </template>
        </FormSection>
    </section>
</template>

<style lang="scss" src="./ScheduleVisitHomeHealth.scss"></style>
<script lang="ts" src="./ScheduleVisitHomeHealth.ts"></script>
